/**
 * BOUTEE KADAI — PREMIUM EFFECTS ENGINE
 * Scroll Reveals · Parallax Momentum · Curtain Transitions · Header Offset
 */

(function () {
  'use strict';

  /* ── 1. SCROLL REVEAL (IntersectionObserver) ──────────── */
  function initScrollReveal() {
    const revealEls = document.querySelectorAll('.bk-reveal');
    if (!revealEls.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -30px 0px' }
    );

    revealEls.forEach((el) => observer.observe(el));
  }

  /* ── 2. CONTENT OFFSET — handles layout & header stacking ── */
  function updateContentOffset() {
    const header      = /** @type {HTMLElement|null} */ (document.getElementById('desiHeader'));
    const announceBar = /** @type {HTMLElement|null} */ (document.querySelector('.bk-announce-bar')) ||
                        /** @type {HTMLElement|null} */ (document.querySelector('.premium-announcement-bar-marquee'));

    if (!header) return;

    const announceH   = (announceBar && announceBar.offsetHeight > 0) ? announceBar.offsetHeight : 0;
    const headerH     = header.offsetHeight;
    const totalOffset = announceH + headerH;

    // Set CSS variables for global use
    document.documentElement.style.setProperty('--announce-height',   announceH + 'px');
    document.documentElement.style.setProperty('--bk-header-height',  headerH + 'px');
    document.documentElement.style.setProperty('--bk-header-offset',  totalOffset + 'px');

    // Update Dawn's layout vars
    document.body.style.setProperty('--header-height',       headerH + 'px');
    document.body.style.setProperty('--header-group-height', totalOffset + 'px');

    // Ensure header sits exactly below announcement bar if not scrolled
    if (window.scrollY <= 20) {
      header.style.top = announceH + 'px';
    }
  }

  /* ── 3. HERO PARALLAX MOMENTUM ────────────────────────── */
  function initHeroParallax() {
    const heroBg      = /** @type {HTMLElement|null} */ (document.querySelector('.bk-hero__media'));
    const heroContent = /** @type {HTMLElement|null} */ (document.querySelector('.bk-hero__content'));
    if (!heroBg || !heroContent) return;

    let ticking = false;

    function updateParallax() {
      if (!heroBg || !heroContent) return;

      const scrollY    = window.scrollY;
      const heroEl     = /** @type {HTMLElement|null} */ (document.querySelector('.bk-hero'));
      const heroHeight = heroEl ? heroEl.offsetHeight : window.innerHeight;

      if (scrollY <= heroHeight) {
        heroBg.style.transform      = `translateY(${scrollY * 0.3}px)`;
        heroContent.style.transform = `translateY(${scrollY * 0.18}px)`;
      }
      ticking = false;
    }

    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          requestAnimationFrame(updateParallax);
          ticking = true;
        }
      },
      { passive: true }
    );
  }

  /* ── 4. PAGE CURTAIN (disabled — was brown full-screen transition on nav) ── */

  /* ── 5. HEADER SHRINK ON SCROLL ───────────────────────── */
  function initHeaderShrink() {
    const header = /** @type {HTMLElement|null} */ (document.getElementById('desiHeader'));
    if (!header) return;

    const isHome = header.dataset.transparent === 'true';
    const announceBar = /** @type {HTMLElement|null} */ (document.querySelector('.bk-announce-bar')) ||
                        /** @type {HTMLElement|null} */ (document.querySelector('.premium-announcement-bar-marquee'));

    window.addEventListener(
      'scroll',
      () => {
        const scrollY   = window.scrollY;
        const announceH = (announceBar && /** @type {HTMLElement} */ (announceBar).offsetHeight) || 0;

        if (scrollY > 80) {
          header.classList.add('is-scrolled');
          header.classList.remove('is-transparent');
          header.style.top = '0';
        } else {
          header.classList.remove('is-scrolled');
          if (isHome) header.classList.add('is-transparent');
          if (announceH > 0) header.style.top = announceH + 'px';
          else header.style.top = '0';
        }
      },
      { passive: true }
    );
  }

  /* ── 6. PRODUCT CARD IMAGE HOVER ──────────────────────── */
  function initProductCardHover() {
    const cards = document.querySelectorAll('.bk-product-card');
    cards.forEach((card) => {
      const primary   = /** @type {HTMLElement|null} */ (card.querySelector('.bk-card-img--primary'));
      const secondary = /** @type {HTMLElement|null} */ (card.querySelector('.bk-card-img--secondary'));
      if (!primary || !secondary) return;

      card.addEventListener('mouseenter', () => {
        primary.style.opacity   = '0';
        secondary.style.opacity = '1';
      });
      card.addEventListener('mouseleave', () => {
        primary.style.opacity   = '1';
        secondary.style.opacity = '0';
      });
    });
  }

  /* ── 7. ANNOUNCEMENT BAR PAUSE ON HOVER ───────────────── */
  function initMarquee() {
    const bar = document.querySelector('.bk-announce-bar') ||
                document.querySelector('.premium-announcement-bar-marquee');
    if (!bar) return;

    const track = bar.querySelector('.bk-announce-track, .marquee-track');
    if (!track) return;

    const trackEl = /** @type {HTMLElement} */ (track);
    bar.addEventListener('mouseenter', () => { trackEl.style.animationPlayState = 'paused'; });
    bar.addEventListener('mouseleave', () => { trackEl.style.animationPlayState = 'running'; });
  }

  /* ── 8. STAGGERED REVEALS ─────────────────────────────── */
  function initStaggeredReveals() {
    const containers = document.querySelectorAll('.bk-stagger-container');
    containers.forEach((container) => {
      const items = container.querySelectorAll('.bk-reveal');
      items.forEach((item, index) => {
        const delay = Math.min(index * 0.1, 0.8);
        /** @type {HTMLElement} */ (item).style.transitionDelay = `${delay}s`;
      });
    });
  }

  /* ── 9. CLEANUP ─────────────────────────────────────────── */
  function cleanLegacyEffects() {
    document.querySelectorAll('.glow').forEach((el) => el.remove());
  }

  /* ── INIT ALL ─────────────────────────────────────────── */
  function init() {
    updateContentOffset();                         // must be first — pushes content below header
    window.addEventListener('resize', updateContentOffset, { passive: true });

    initScrollReveal();
    initStaggeredReveals();
    initHeroParallax();
    initHeaderShrink();
    initProductCardHover();
    initMarquee();
    cleanLegacyEffects();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
